import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Link, useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import {
  AlertTriangle, ChevronLeft, ChevronRight, ExternalLink, FileImage, Filter, Maximize2,
  Minus, Newspaper, Plus, RefreshCw, Search, X,
} from "lucide-react";

import {
  FACET_KEYS, fetchPrintArticle, fetchPrintStats, searchPrint,
  type ArticleResponse, type FacetBucket, type FacetKey, type SearchHit, type SearchMode,
  type SearchParams, type SearchSort, type StatsResponse,
} from "../api/printSearch";
import { explainError } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { Spinner } from "../components/Spinner";
import { shortDate, timeSince } from "../lib/format";

/* ──────────────────────────────────────────────────────────────────────
   /print-search — full-text search over every OCR'd article crop.

   One ES document per article (pipeline/ocr → print-articles). The whole
   search state lives in the URL, so a search can be pasted to a colleague
   and the back button walks through refinements. SuperAdmin + Admin — the
   route is wrapped in RequireGroup and the API 403s everyone else.

   Highlight HTML from the API is escaped server-side with <mark> as the
   only surviving tag (api/routers/print_search.py: sanitize_fragment), which
   is why dangerouslySetInnerHTML is acceptable here and nowhere else.
   ────────────────────────────────────────────────────────────────────── */

const PAGE_SIZE = 20;

const MODE_LABEL: Record<SearchMode, { label: string; hint: string }> = {
  any:    { label: "Any words",      hint: "Any of the words, case- and accent-insensitive. Whole-phrase matches rank first." },
  phrase: { label: "Phrase",         hint: "The words together, in this order (case-insensitive)." },
  case:   { label: "Case-sensitive", hint: "Phrase match that respects capitals — 'BJP' will not match 'bjp'." },
  exact:  { label: "Exact",          hint: "Exact whitespace-separated tokens, punctuation and case as printed." },
};
const SORT_LABEL: Record<SearchSort, string> = { relevance: "Relevance", newest: "Newest", circulation: "Circulation" };

const FACET_TITLE: Record<FacetKey, string> = {
  category: "Category", language: "Language", channel: "Publisher", edition: "Edition",
  state: "State", kind: "Kind", page: "Page",
};
// URL keys — short, and none of them collide with the TopBar's `?date=`.
const URL_KEY: Record<FacetKey, string> = {
  category: "cat", language: "lang", channel: "ch", edition: "ed", state: "st", kind: "kind", page: "pg",
};
const OWN_KEYS = ["q", "mode", "sort", "p", "df", "dt", "minc", "a", ...Object.values(URL_KEY)];

// ── URL <-> state ─────────────────────────────────────────────────────────────────────────────

function readParams(sp: URLSearchParams): SearchParams {
  const mode = sp.get("mode") as SearchMode | null;
  const sort = sp.get("sort") as SearchSort | null;
  const filters: SearchParams["filters"] = {};
  for (const k of FACET_KEYS) {
    const v = sp.getAll(URL_KEY[k]).filter(Boolean);
    if (v.length) filters[k] = v;
  }
  const page = Math.max(1, Number(sp.get("p")) || 1);
  const minc = Number(sp.get("minc")) || 0;
  return {
    q: sp.get("q") ?? "",
    mode: mode && mode in MODE_LABEL ? mode : "any",
    sort: sort && sort in SORT_LABEL ? sort : "relevance",
    page,
    size: PAGE_SIZE,
    date_from: sp.get("df") || undefined,
    date_to: sp.get("dt") || undefined,
    min_chars: minc > 0 ? minc : undefined,
    filters,
  };
}

function writeParams(base: URLSearchParams, p: SearchParams, selected: number | null): URLSearchParams {
  const u = new URLSearchParams(base);
  for (const k of OWN_KEYS) u.delete(k);
  if (p.q) u.set("q", p.q);
  if (p.mode !== "any") u.set("mode", p.mode);
  if (p.sort !== "relevance") u.set("sort", p.sort);
  if (p.page > 1) u.set("p", String(p.page));
  if (p.date_from) u.set("df", p.date_from);
  if (p.date_to) u.set("dt", p.date_to);
  if (p.min_chars) u.set("minc", String(p.min_chars));
  for (const k of FACET_KEYS) for (const v of p.filters[k] ?? []) u.append(URL_KEY[k], v);
  if (selected) u.set("a", String(selected));
  return u;
}

// ── formatting ────────────────────────────────────────────────────────────────────────────────

const nf = new Intl.NumberFormat("en-IN");
function num(n: number | null | undefined): string {
  return n === null || n === undefined ? "—" : nf.format(n);
}
function compact(n: number | null | undefined): string {
  if (n === null || n === undefined) return "—";
  if (n >= 10_000_000) return `${(n / 10_000_000).toFixed(1)} cr`;
  if (n >= 100_000) return `${(n / 100_000).toFixed(1)} L`;
  if (n >= 1000) return `${(n / 1000).toFixed(0)}k`;
  return String(n);
}
function dur(s: number | null | undefined): string {
  if (s === null || s === undefined || !isFinite(s)) return "—";
  if (s < 90) return `${s.toFixed(s < 10 ? 1 : 0)} s`;
  if (s < 5400) return `${Math.round(s / 60)} min`;
  return `${(s / 3600).toFixed(1)} h`;
}
function ist(iso: unknown): string {
  if (typeof iso !== "string" || !iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-IN", {
    timeZone: "Asia/Kolkata", day: "2-digit", month: "short",
    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false,
  });
}
function str(v: unknown): string | null {
  if (v === null || v === undefined || v === "") return null;
  return String(v);
}

// ── page ──────────────────────────────────────────────────────────────────────────────────────

export function PrintSearch() {
  const [sp, setSp] = useSearchParams();
  const params = useMemo(() => readParams(sp), [sp]);
  const selected = Number(sp.get("a")) || null;

  const [draft, setDraft] = useState(params.q);
  useEffect(() => setDraft(params.q), [params.q]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [pageModal, setPageModal] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsTop = useRef<HTMLDivElement>(null);

  const go = useCallback(
    (patch: Partial<SearchParams>, opts: { keepPage?: boolean; select?: number | null } = {}) => {
      const next: SearchParams = { ...params, ...patch, page: opts.keepPage ? (patch.page ?? params.page) : 1 };
      const sel = opts.select !== undefined ? opts.select : opts.keepPage ? selected : null;
      setSp(writeParams(sp, next, sel));
    },
    [params, selected, sp, setSp],
  );
  const select = useCallback(
    (id: number | null) => setSp(writeParams(sp, params, id), { replace: true }),
    [sp, params, setSp],
  );

  const res = useQuery({
    queryKey: ["print-search", params],
    queryFn: ({ signal }) => searchPrint(params, signal),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
  const stats = useQuery({ queryKey: ["print-search-stats"], queryFn: fetchPrintStats, refetchInterval: 60_000 });

  // Esc: the full-page modal first, then the detail panel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (pageModal) { setPageModal(false); return; }
      if (selected) select(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pageModal, selected, select]);

  // "/" focuses the search box, like most search UIs.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (e.key === "/" && t && !["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName)) {
        e.preventDefault();
        inputRef.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  function submit(e: FormEvent) {
    e.preventDefault();
    const q = draft.trim();
    // A fresh text query is usually best by relevance; keep an explicit non-default sort.
    go({ q });
  }

  function toggleFacet(k: FacetKey, v: string) {
    const cur = params.filters[k] ?? [];
    const nextVals = cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v];
    go({ filters: { ...params.filters, [k]: nextVals } });
  }

  const activeFilterCount =
    FACET_KEYS.reduce((n, k) => n + (params.filters[k]?.length ?? 0), 0) +
    (params.date_from ? 1 : 0) + (params.date_to ? 1 : 0) + (params.min_chars ? 1 : 0);

  function clearFilters() {
    go({ filters: {}, date_from: undefined, date_to: undefined, min_chars: undefined });
  }

  function toPage(n: number) {
    go({ page: n }, { keepPage: true, select: null });
    resultsTop.current?.scrollIntoView({ block: "start", behavior: "smooth" });
  }

  const data = res.data;
  const totalPages = data ? Math.max(1, Math.ceil(Math.min(data.total, 10_000) / PAGE_SIZE)) : 1;

  return (
    <div className="flex flex-col gap-4 min-w-0">
      {/* ── masthead ───────────────────────────────────────────────── */}
      <header className="flex items-end justify-between flex-wrap gap-3 pb-2 border-b" style={{ borderColor: "var(--rule)" }}>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] mb-1">Print · Search</div>
          <h1 className="font-display text-[26px] leading-tight">Search the papers</h1>
        </div>
        <StatsStrip stats={stats.data} error={stats.error} loading={stats.isLoading} />
      </header>

      {/* ── search bar ─────────────────────────────────────────────── */}
      <form onSubmit={submit} className="np-card px-4 py-3 flex flex-col gap-3" role="search">
        <div className="flex items-center gap-3">
          <Search size={20} className="text-[var(--fg-faint)] shrink-0" aria-hidden />
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Search headlines and article text…"
            aria-label="Search text"
            maxLength={500}
            className="flex-1 min-w-0 bg-transparent outline-none font-display text-[20px] md:text-[24px] py-1 placeholder:italic placeholder:text-[var(--fg-faint)]"
            autoFocus
          />
          {draft && (
            <button type="button" onClick={() => { setDraft(""); inputRef.current?.focus(); }}
              className="text-[var(--fg-faint)] hover:text-[var(--fg)]" title="Clear" aria-label="Clear search text">
              <X size={16} />
            </button>
          )}
          <button type="submit" className="np-btn !px-4 !py-2 shrink-0">
            <Search size={13} /> <span className="hidden sm:inline">Search</span>
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <div className="flex flex-wrap" role="group" aria-label="Match mode">
            {(Object.keys(MODE_LABEL) as SearchMode[]).map((m) => (
              <button key={m} type="button" className="np-seg" aria-pressed={params.mode === m}
                title={MODE_LABEL[m].hint}
                onClick={() => go({ mode: m, q: draft.trim() })}>
                {MODE_LABEL[m].label}
              </button>
            ))}
          </div>
          <label className="flex items-center gap-2 font-mono text-[11px] text-[var(--fg-muted)]">
            <span className="uppercase tracking-folio text-[10px] text-[var(--fg-faint)]">sort</span>
            <select value={params.sort} onChange={(e) => go({ sort: e.target.value as SearchSort })}
              className="bg-transparent border px-2 py-1 font-mono text-[11px]" style={{ borderColor: "var(--rule)" }}>
              {(Object.keys(SORT_LABEL) as SearchSort[]).map((s) => <option key={s} value={s}>{SORT_LABEL[s]}</option>)}
            </select>
          </label>
          <span className="font-sans text-[12px] text-[var(--fg-faint)] italic hidden md:inline">
            {MODE_LABEL[params.mode].hint}
          </span>
          <button type="button" onClick={() => setFiltersOpen((o) => !o)}
            className="np-btn-ghost !px-3 !py-1 lg:hidden ml-auto">
            <Filter size={12} /> filters{activeFilterCount ? ` (${activeFilterCount})` : ""}
          </button>
        </div>
      </form>

      {/* ── body: filters | results | detail ───────────────────────── */}
      <div className={
        "grid gap-4 items-start min-w-0 " +
        (selected ? "lg:grid-cols-[230px_minmax(0,1fr)_minmax(0,1.15fr)]" : "lg:grid-cols-[230px_minmax(0,1fr)]")
      }>
        <aside className={(filtersOpen ? "block" : "hidden") + " lg:block min-w-0"}>
          <Filters
            params={params}
            facets={data?.facets}
            activeCount={activeFilterCount}
            onToggle={toggleFacet}
            onDate={(df, dt) => go({ date_from: df, date_to: dt })}
            onMinChars={(n) => go({ min_chars: n || undefined })}
            onClear={clearFilters}
          />
        </aside>

        <section className="min-w-0 flex flex-col gap-3" ref={resultsTop}>
          <ResultsHeader
            loading={res.isFetching}
            total={data?.total}
            relation={data?.total_relation}
            took={data?.took_ms}
            page={params.page}
            onRefresh={() => res.refetch()}
          />

          {res.error && (
            <div className="np-banner flex items-start gap-2">
              <AlertTriangle size={14} className="text-[var(--accent)] shrink-0 mt-0.5" />
              <span>{explainError(res.error)}</span>
            </div>
          )}
          {res.isLoading && <div className="py-16 flex justify-center"><Spinner label="Searching" /></div>}

          {data && data.hits.length === 0 && !res.error && (
            <EmptyState
              indexEmpty={!params.q && activeFilterCount === 0 && data.total === 0}
              hasFilters={activeFilterCount > 0}
              mode={params.mode}
              onClear={clearFilters}
              onAnyWords={() => go({ mode: "any" })}
            />
          )}

          <ol className={"flex flex-col gap-2 transition-opacity " + (res.isFetching && !res.isLoading ? "opacity-60" : "")}>
            {data?.hits.map((h) => (
              <li key={h.article_id}>
                <ResultCard hit={h} active={selected === h.article_id} onOpen={() => select(h.article_id)} />
              </li>
            ))}
          </ol>

          {data && data.total > PAGE_SIZE && (
            <nav className="flex items-center justify-between gap-2 pt-2" aria-label="Pagination">
              <button className="np-btn-ghost" disabled={params.page <= 1} onClick={() => toPage(params.page - 1)}>
                <ChevronLeft size={13} /> prev
              </button>
              <span className="font-mono text-[11px] text-[var(--fg-muted)]">
                page {params.page} of {num(totalPages)}
              </span>
              <button className="np-btn-ghost" disabled={params.page >= totalPages} onClick={() => toPage(params.page + 1)}>
                next <ChevronRight size={13} />
              </button>
            </nav>
          )}
        </section>

        {selected && (
          <DetailPanel
            key={selected}
            id={selected}
            q={params.q}
            mode={params.mode}
            onClose={() => select(null)}
            onFullPage={() => setPageModal(true)}
            pageModal={pageModal}
            onClosePage={() => setPageModal(false)}
          />
        )}
      </div>
    </div>
  );
}

// ── stats strip ───────────────────────────────────────────────────────────────────────────────

function StatsStrip({ stats, error, loading }: { stats?: StatsResponse; error: unknown; loading: boolean }) {
  if (loading) return <span className="font-mono text-[11px] text-[var(--fg-faint)]">index stats…</span>;
  if (error || !stats) {
    return <span className="font-mono text-[11px] text-[var(--accent)]">index stats unavailable — {explainError(error)}</span>;
  }
  const lastMs = stats.last_indexed_at ? Date.now() - new Date(stats.last_indexed_at).getTime() : null;
  const live = lastMs !== null && lastMs < 15 * 60_000;
  const latest = stats.by_date[0];
  return (
    <div className="flex flex-wrap items-baseline gap-x-5 gap-y-1">
      <Stat label="indexed" value={num(stats.total_docs)} strong />
      {latest && <Stat label={shortDate(latest.date).replace(/^\w+, /, "")} value={num(latest.count)} />}
      <Stat label="publishers" value={num(stats.publishers)} />
      <Stat label="editions" value={num(stats.editions)} />
      <Stat label="last hour" value={num(stats.indexed_last_hour)} />
      {stats.avg_crawl_to_indexed_s !== null && <Stat label="crawl → indexed" value={dur(stats.avg_crawl_to_indexed_s)} />}
      <span className="flex items-center gap-1.5 font-mono text-[11px] text-[var(--fg-muted)]"
        title={stats.last_indexed_at ? `last document indexed ${ist(stats.last_indexed_at)} IST` : "nothing indexed yet"}>
        <span className="inline-block w-1.5 h-1.5" style={{ background: live ? "#3f8f4f" : "var(--fg-faint)" }} aria-hidden />
        {stats.last_indexed_at ? `last ${timeSince(stats.last_indexed_at)}` : "never"}
      </span>
    </div>
  );
}

function Stat({ label, value, strong }: { label: string; value: ReactNode; strong?: boolean }) {
  return (
    <span className="flex items-baseline gap-1.5">
      <span className={"font-mono tabular-nums " + (strong ? "text-[15px] text-[var(--fg)]" : "text-[13px] text-[var(--fg)]")}>{value}</span>
      <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)]">{label}</span>
    </span>
  );
}

// ── filters ───────────────────────────────────────────────────────────────────────────────────

function Filters({
  params, facets, activeCount, onToggle, onDate, onMinChars, onClear,
}: {
  params: SearchParams;
  facets?: Record<string, FacetBucket[]>;
  activeCount: number;
  onToggle: (k: FacetKey, v: string) => void;
  onDate: (df?: string, dt?: string) => void;
  onMinChars: (n: number) => void;
  onClear: () => void;
}) {
  const days = facets?.business_date ?? [];
  return (
    <div className="np-card px-4 py-3 flex flex-col gap-4 lg:sticky lg:top-2">
      <div className="flex items-center justify-between">
        <span className="np-label">Filters</span>
        {activeCount > 0 && (
          <button className="font-mono text-[10px] uppercase tracking-folio text-[var(--accent)] hover:underline" onClick={onClear}>
            clear {activeCount}
          </button>
        )}
      </div>

      <FacetSection title="Business date">
        <div className="grid grid-cols-2 gap-2">
          <DateInput label="from" value={params.date_from} onChange={(v) => onDate(v, params.date_to)} />
          <DateInput label="to" value={params.date_to} onChange={(v) => onDate(params.date_from, v)} />
        </div>
        {days.length > 0 && (
          <ul className="mt-2 flex flex-col">
            {days.slice(0, 7).map((d) => {
              const on = params.date_from === d.value && params.date_to === d.value;
              return (
                <li key={d.value}>
                  <button
                    onClick={() => (on ? onDate(undefined, undefined) : onDate(d.value, d.value))}
                    className={"w-full flex justify-between items-baseline py-0.5 font-mono text-[11px] " +
                      (on ? "text-[var(--accent)]" : "text-[var(--fg-muted)] hover:text-[var(--fg)]")}>
                    <span>{shortDate(d.value)}</span><span className="tabular-nums">{num(d.count)}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </FacetSection>

      {(["category", "language", "channel", "edition", "state", "kind"] as FacetKey[]).map((k) => (
        <FacetList key={k} title={FACET_TITLE[k]} buckets={facets?.[k] ?? []}
          selected={params.filters[k] ?? []} onToggle={(v) => onToggle(k, v)} />
      ))}

      <FacetSection title="Page">
        <div className="flex flex-wrap gap-1">
          {mergeSelected(facets?.page ?? [], params.filters.page ?? [], (v) => `p${v}`).map((b) => {
            const on = (params.filters.page ?? []).includes(b.value);
            return (
              <button key={b.value} onClick={() => onToggle("page", b.value)} title={`${num(b.count)} articles`}
                className="px-1.5 py-0.5 font-mono text-[10px] border"
                style={{
                  borderColor: on ? "var(--primary)" : "var(--rule)",
                  background: on ? "var(--primary)" : "transparent",
                  color: on ? "var(--bg)" : "var(--fg-muted)",
                }}>
                {b.label}
              </button>
            );
          })}
        </div>
      </FacetSection>

      <FacetSection title="Minimum length">
        <select value={params.min_chars ?? 0} onChange={(e) => onMinChars(Number(e.target.value))}
          className="w-full bg-transparent border px-2 py-1 font-mono text-[11px]" style={{ borderColor: "var(--rule)" }}>
          {[0, 200, 500, 1000, 2000, 4000].map((n) => (
            <option key={n} value={n}>{n ? `${num(n)}+ characters` : "any length"}</option>
          ))}
        </select>
      </FacetSection>
    </div>
  );
}

function FacetSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div>
      <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] mb-1.5">{title}</div>
      {children}
    </div>
  );
}

function DateInput({ label, value, onChange }: { label: string; value?: string; onChange: (v?: string) => void }) {
  return (
    <label className="flex flex-col gap-0.5 min-w-0">
      <span className="font-mono text-[9px] uppercase tracking-folio text-[var(--fg-faint)]">{label}</span>
      <input type="date" value={value ?? ""} onChange={(e) => onChange(e.target.value || undefined)}
        className="w-full min-w-0 bg-transparent border px-1 py-0.5 font-mono text-[11px]" style={{ borderColor: "var(--rule)" }} />
    </label>
  );
}

/** Selected values that dropped out of the top-N buckets (or have 0 hits now) must still be
 *  visible, or the user cannot un-tick them. */
function mergeSelected(buckets: FacetBucket[], selected: string[], label: (v: string) => string): FacetBucket[] {
  const have = new Set(buckets.map((b) => b.value));
  const extra = selected.filter((v) => !have.has(v)).map((v) => ({ value: v, label: label(v), count: 0 }));
  return [...extra, ...buckets];
}

function FacetList({ title, buckets, selected, onToggle }: {
  title: string; buckets: FacetBucket[]; selected: string[]; onToggle: (v: string) => void;
}) {
  const [all, setAll] = useState(false);
  const rows = mergeSelected(buckets, selected, (v) => v);
  if (rows.length === 0) return null;
  const shown = all ? rows : rows.slice(0, 7);
  return (
    <FacetSection title={title}>
      <ul className="flex flex-col">
        {shown.map((b) => {
          const on = selected.includes(b.value);
          return (
            <li key={b.value}>
              <label className="flex items-start gap-2 py-0.5 cursor-pointer group">
                <input type="checkbox" checked={on} onChange={() => onToggle(b.value)} className="mt-[3px] shrink-0 accent-[var(--primary)]" />
                <span className={"flex-1 min-w-0 font-sans text-[12.5px] leading-snug break-words " +
                  (on ? "text-[var(--fg)] font-medium" : "text-[var(--fg-muted)] group-hover:text-[var(--fg)]")}>
                  {b.label}
                </span>
                <span className="font-mono text-[10px] tabular-nums text-[var(--fg-faint)] mt-[2px]">{num(b.count)}</span>
              </label>
            </li>
          );
        })}
      </ul>
      {rows.length > 7 && (
        <button onClick={() => setAll((a) => !a)} className="mt-1 font-mono text-[10px] uppercase tracking-folio text-[var(--primary)] hover:underline">
          {all ? "fewer" : `all ${rows.length}`}
        </button>
      )}
    </FacetSection>
  );
}

// ── results ───────────────────────────────────────────────────────────────────────────────────

function ResultsHeader({ loading, total, relation, took, page, onRefresh }: {
  loading: boolean; total?: number; relation?: string; took?: number | null; page: number; onRefresh: () => void;
}) {
  const from = total ? (page - 1) * PAGE_SIZE + 1 : 0;
  const to = total ? Math.min(total, page * PAGE_SIZE) : 0;
  return (
    <div className="flex items-baseline justify-between gap-2 flex-wrap">
      <div className="font-mono text-[11px] text-[var(--fg-muted)]">
        {total === undefined ? "—" : total === 0 ? "no articles" : (
          <>
            <span className="text-[var(--fg)] text-[13px] tabular-nums">{num(total)}{relation === "gte" ? "+" : ""}</span>
            {" "}article{total === 1 ? "" : "s"}
            {total > PAGE_SIZE && <> · showing {num(from)}–{num(to)}</>}
          </>
        )}
        {took !== undefined && took !== null && <span className="text-[var(--fg-faint)]"> · {took} ms</span>}
      </div>
      <button onClick={onRefresh} className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] hover:text-[var(--fg)] flex items-center gap-1" title="Re-run search">
        <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> refresh
      </button>
    </div>
  );
}

function ResultCard({ hit: h, active, onOpen }: { hit: SearchHit; active: boolean; onOpen: () => void }) {
  const [imgFailed, setImgFailed] = useState(false);
  const meta = [
    h.channel_name,
    h.edition_name,
    h.page_no !== null ? `p${h.display_page_no || h.page_no}${h.page_count ? `/${h.page_count}` : ""}` : null,
    h.business_date ? shortDate(h.business_date) : null,
    h.language,
    h.circulation ? `circ ${compact(h.circulation)}` : null,
  ].filter(Boolean);
  return (
    <article
      role="button"
      tabIndex={0}
      aria-pressed={active}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(); } }}
      className="np-card flex gap-3 p-3 cursor-pointer transition-colors hover:border-[var(--rule-strong)]"
      style={active ? { borderColor: "var(--primary)", boxShadow: "inset 3px 0 0 var(--accent)" } : undefined}
    >
      <div className="w-[72px] sm:w-[92px] h-[100px] sm:h-[124px] shrink-0 overflow-hidden np-card-inset flex items-center justify-center">
        {h.crop_url && !imgFailed ? (
          <img src={h.crop_url} alt="" loading="lazy" decoding="async" onError={() => setImgFailed(true)}
            className="w-full h-full object-cover object-top" />
        ) : (
          <FileImage size={20} className="text-[var(--fg-faint)]" aria-label="no crop image" />
        )}
      </div>
      <div className="min-w-0 flex-1 flex flex-col gap-1">
        <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--fg-faint)] break-words">
          {meta.join(" · ")}
        </div>
        <h3 className="np-hl font-display text-[16px] sm:text-[17px] leading-snug text-[var(--fg)] break-words"
          dangerouslySetInnerHTML={{ __html: h.headline_html || "<em>Untitled article</em>" }} />
        {h.snippets_html.slice(0, 2).map((s, i) => (
          <p key={i} className="np-hl font-sans text-[13px] leading-relaxed text-[var(--fg-muted)] line-clamp-3 break-words"
            dangerouslySetInnerHTML={{ __html: `${s}…` }} />
        ))}
        <div className="flex flex-wrap gap-1.5 mt-0.5">
          {h.category && <span className="np-chip">{h.category}</span>}
          {h.kind && h.kind !== "news" && <span className="np-chip">{h.kind}</span>}
          {h.is_lead && <span className="np-chip"><span className="np-chip-dot" />lead</span>}
          {h.word_count !== null && <span className="font-mono text-[10px] text-[var(--fg-faint)] self-center">{num(h.word_count)} words</span>}
        </div>
      </div>
    </article>
  );
}

function EmptyState({ indexEmpty, hasFilters, mode, onClear, onAnyWords }: {
  indexEmpty: boolean; hasFilters: boolean; mode: SearchMode; onClear: () => void; onAnyWords: () => void;
}) {
  return (
    <div className="np-card px-6 py-10 text-center flex flex-col items-center gap-3">
      <Newspaper size={28} className="text-[var(--fg-faint)]" />
      {indexEmpty ? (
        <>
          <div className="font-display text-[20px]">Nothing indexed yet</div>
          <p className="font-sans text-[13px] text-[var(--fg-muted)] max-w-md">
            The search index is empty. Articles appear here a few seconds after the OCR pipeline finishes a crop.
          </p>
        </>
      ) : (
        <>
          <div className="font-display text-[20px]">No articles match</div>
          <p className="font-sans text-[13px] text-[var(--fg-muted)] max-w-md">
            {mode !== "any" ? `“${MODE_LABEL[mode].label}” is strict about word order${mode === "phrase" ? "" : " and case"}. ` : ""}
            {hasFilters ? "Filters are narrowing the results too." : "Try fewer or different words."}
          </p>
          <div className="flex gap-2 flex-wrap justify-center">
            {mode !== "any" && <button className="np-btn-ghost" onClick={onAnyWords}>search any words</button>}
            {hasFilters && <button className="np-btn-ghost" onClick={onClear}>clear filters</button>}
          </div>
        </>
      )}
    </div>
  );
}

// ── detail panel ──────────────────────────────────────────────────────────────────────────────

function DetailPanel({ id, q, mode, onClose, onFullPage, pageModal, onClosePage }: {
  id: number; q: string; mode: SearchMode; onClose: () => void; onFullPage: () => void;
  pageModal: boolean; onClosePage: () => void;
}) {
  const art = useQuery({
    queryKey: ["print-article", id, q, mode],
    queryFn: () => fetchPrintArticle(id, q, mode),
    staleTime: 5 * 60_000,
  });
  const a = art.data;
  const d = a?.doc;
  // Details, pipeline times and the engine alias are SuperAdmin-only; the API does not even send
  // them to Admin. No model names reach the browser for anyone (api/routers/print_search.py).
  const { me } = useAuth();
  const isSuper = me?.group_name === "SuperAdmin";

  return (
    <aside
      className="fixed inset-0 z-40 overflow-y-auto lg:z-auto lg:sticky lg:top-2 lg:max-h-[calc(100vh-7rem)] min-w-0"
      style={{ background: "var(--bg)" }}
      aria-label="Article detail"
    >
      <div className="np-card min-h-full lg:min-h-0 flex flex-col">
        <div className="sticky top-0 z-10 flex items-center gap-2 px-4 py-2 border-b" style={{ borderColor: "var(--rule)", background: "var(--bg-card)" }}>
          <span className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] flex-1 truncate">
            Article {id}{d?.article_no ? ` · a${String(d.article_no).padStart(2, "0")}` : ""}
          </span>
          {a?.page_url && (
            <button className="np-btn !px-3 !py-1.5 !text-[11px]" onClick={onFullPage}>
              <Maximize2 size={12} /> full page
            </button>
          )}
          {d?.issue_id ? (
            <Link to={`/reader/${d.issue_id}`} className="np-btn-ghost !px-2 !py-1.5 hidden sm:inline-flex" title="Open this issue in the reader">
              <Newspaper size={12} /> reader
            </Link>
          ) : null}
          <button onClick={onClose} className="np-btn-ghost !px-2 !py-1.5" title="Close (Esc)" aria-label="Close article">
            <X size={14} />
          </button>
        </div>

        {art.isLoading && <div className="py-16 flex justify-center"><Spinner label="Loading article" /></div>}
        {art.error && (
          <div className="m-4 np-banner flex items-start gap-2">
            <AlertTriangle size={14} className="text-[var(--accent)] shrink-0 mt-0.5" />
            <span>{explainError(art.error)}</span>
          </div>
        )}

        {a && d && (
          <div className="px-4 py-4 flex flex-col gap-5 min-w-0">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--fg-faint)] mb-1 break-words">
                {[str(d.channel_name), str(d.edition_name),
                  d.page_no !== undefined ? `page ${str(d.display_page_no) ?? str(d.page_no)}${d.page_count ? ` of ${str(d.page_count)}` : ""}` : null,
                  d.business_date ? shortDate(String(d.business_date)) : null].filter(Boolean).join(" · ")}
              </div>
              <h2 className="np-hl font-display text-[22px] leading-tight break-words"
                dangerouslySetInnerHTML={{ __html: a.headline_html ?? escapeHtml(d.headline ?? "Untitled article") }} />
            </div>

            {a.crop_url && <CropViewer url={a.crop_url} w={Number(d.crop_w) || null} />}

            <section>
              <SectionLabel>Full text <span className="normal-case tracking-normal text-[var(--fg-faint)]">· {num(Number(d.word_count) || null)} words{isSuper && d.ocr_alias ? ` · ${str(d.ocr_alias)}` : ""}</span></SectionLabel>
              {a.body_html ? (
                <div className="np-hl font-sans text-[14px] leading-relaxed whitespace-pre-wrap break-words"
                  dangerouslySetInnerHTML={{ __html: a.body_html }} />
              ) : (
                <div className="font-sans text-[14px] leading-relaxed whitespace-pre-wrap break-words">
                  {d.body_text || d.full_text || <em className="text-[var(--fg-faint)]">No text was recognised.</em>}
                </div>
              )}
            </section>

            {isSuper && (<>
            <section>
              <SectionLabel>Details</SectionLabel>
              <MetaGrid rows={[
                ["Publisher", str(d.channel_name)],
                ["Edition", [str(d.edition_name), str(d.edition_alias)].filter(Boolean).join(" — ") || null],
                ["Place", [str(d.city), str(d.state), str(d.zone)].filter(Boolean).join(", ") || null],
                ["Language", str(d.language)],
                ["Category", str(d.category)],
                ["Circulation", d.circulation ? num(Number(d.circulation)) : null],
                ["Readership", d.readership ? num(Number(d.readership)) : null],
                ["Kind", [str(d.kind), d.is_lead ? "lead" : null, d.is_above_fold ? "above fold" : null].filter(Boolean).join(" · ") || null],
                ["Position", [str(d.position_zone), d.area_pct ? `${Number(d.area_pct).toFixed(1)}% of page` : null].filter(Boolean).join(" · ") || null],
                ["Box", d.bbox_w ? `${d.bbox_x},${d.bbox_y} · ${d.bbox_w}×${d.bbox_h}px${d.box_confidence ? ` · conf ${Number(d.box_confidence).toFixed(2)}` : ""}` : null],
                ["Length", `${num(Number(d.char_count) || null)} chars`],
                ["OCR", str(d.ocr_alias)],
                ["Crop", str(d.crop_alias)],
              ]} />
            </section>

            <section>
              <SectionLabel>Pipeline timeline <span className="normal-case tracking-normal text-[var(--fg-faint)]">· IST</span></SectionLabel>
              <MetaGrid rows={[
                ["Crawled", ist(d.crawled_at)],
                ["Crop queued", `${ist(d.crop_queued_at)}  (+${dur(Number(d.t_crawl_to_crop_queue_s))})`],
                ["Page cropped", `${ist(d.page_cropped_at)}  (${dur(Number(d.t_page_crop_s))}, waited ${dur(Number(d.t_crop_wait_s))})`],
                ["OCR started", `${ist(d.ocr_started_at)}  (waited ${dur(Number(d.t_ocr_wait_s))})`],
                ["OCR done", `${ist(d.ocr_done_at)}  (${dur(Number(d.t_ocr_s))})`],
                ["Indexed", `${ist(d.es_indexed_at)}  (crawl → indexed ${dur(Number(d.t_crawl_to_indexed_s))})`],
              ]} />
            </section>

            <details className="font-mono text-[10px] text-[var(--fg-muted)]">
              <summary className="cursor-pointer uppercase tracking-folio text-[var(--fg-faint)]">source paths</summary>
              <div className="mt-2 flex flex-col gap-1 break-all">
                <span>page: {str(d.page_path_full) ?? "—"}</span>
                {a.crop_url && <a className="np-link" href={a.crop_url} target="_blank" rel="noopener noreferrer">crop image <ExternalLink size={9} className="inline" /></a>}
                {a.page_url && <a className="np-link" href={a.page_url} target="_blank" rel="noopener noreferrer">page image <ExternalLink size={9} className="inline" /></a>}
              </div>
            </details>
            </>)}
          </div>
        )}
      </div>

      {pageModal && a && createPortal(<PageModal art={a} onClose={onClosePage} />, document.body)}
    </aside>
  );
}

function SectionLabel({ children }: { children: ReactNode }) {
  return <div className="font-mono text-[10px] uppercase tracking-folio text-[var(--fg-faint)] mb-2 pb-1 border-b" style={{ borderColor: "var(--rule)" }}>{children}</div>;
}

function MetaGrid({ rows }: { rows: Array<[string, string | null]> }) {
  return (
    <dl className="grid grid-cols-[92px_minmax(0,1fr)] gap-x-3 gap-y-1">
      {rows.filter(([, v]) => v && v !== "—").map(([k, v]) => (
        <div key={k} className="contents">
          <dt className="font-mono text-[10px] uppercase tracking-[0.08em] text-[var(--fg-faint)] pt-[2px]">{k}</dt>
          <dd className="font-sans text-[12.5px] text-[var(--fg)] break-words">{v}</dd>
        </div>
      ))}
    </dl>
  );
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

// ── crop viewer ───────────────────────────────────────────────────────────────────────────────

const CROP_ZOOMS = [1, 1.5, 2, 3] as const;

function CropViewer({ url, w }: { url: string; w: number | null }) {
  const [z, setZ] = useState(0);              // index into CROP_ZOOMS; 0 = fit width
  const [failed, setFailed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const zoom = CROP_ZOOMS[z] ?? 1;
  if (failed) {
    return (
      <div className="np-card-inset px-4 py-6 text-center font-mono text-[11px] text-[var(--fg-muted)]">
        Crop image could not be loaded. <a className="np-link" href={url} target="_blank" rel="noopener noreferrer">open directly</a>
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center gap-1 justify-end">
        <span className="font-mono text-[10px] text-[var(--fg-faint)] mr-auto">{zoom === 1 ? "fit" : `${zoom}×`}{w ? ` · ${w}px wide` : ""}</span>
        <button className="np-btn-ghost !px-2 !py-1" disabled={z === 0} onClick={() => setZ((v) => Math.max(0, v - 1))} aria-label="Zoom out"><Minus size={12} /></button>
        <button className="np-btn-ghost !px-2 !py-1" disabled={z === CROP_ZOOMS.length - 1} onClick={() => setZ((v) => Math.min(CROP_ZOOMS.length - 1, v + 1))} aria-label="Zoom in"><Plus size={12} /></button>
        <a className="np-btn-ghost !px-2 !py-1" href={url} target="_blank" rel="noopener noreferrer" title="Open crop in a new tab"><ExternalLink size={12} /></a>
      </div>
      <div className="np-card-inset overflow-auto max-h-[70vh] relative" style={{ cursor: z === 0 ? "zoom-in" : "zoom-out" }}
        onClick={() => setZ((v) => (v === 0 ? 2 : 0))}>
        {!loaded && <div className="absolute inset-0 flex items-center justify-center"><Spinner /></div>}
        <img src={url} alt="Article crop" onLoad={() => setLoaded(true)} onError={() => setFailed(true)}
          style={{ width: `${zoom * 100}%`, maxWidth: "none", display: "block" }} />
      </div>
    </div>
  );
}

// ── full page modal ───────────────────────────────────────────────────────────────────────────

function PageModal({ art, onClose }: { art: ArticleResponse; onClose: () => void }) {
  const d = art.doc;
  const [fit, setFit] = useState<"width" | "height">("width");
  const [nat, setNat] = useState<{ w: number; h: number } | null>(null);
  const [failed, setFailed] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // The article box is in page pixels (page_w × page_h). Using those as the SVG viewBox makes
  // the overlay scale with the displayed image for free — displayed / page_w — whatever the
  // served image's own resolution.
  const vw = Number(d.page_w) || nat?.w || 0;
  const vh = Number(d.page_h) || nat?.h || 0;
  const bx = Number(d.bbox_x) || 0, by = Number(d.bbox_y) || 0;
  const bw = Number(d.bbox_w) || 0, bh = Number(d.bbox_h) || 0;
  const poly = art.polygon;
  const hole = poly && poly.length >= 3
    ? `M${poly.map((p) => `${p[0]},${p[1]}`).join("L")}Z`
    : bw ? `M${bx},${by}h${bw}v${bh}h${-bw}Z` : "";

  // Bring the article into view once the page has a size.
  useEffect(() => {
    const img = imgRef.current, sc = scroller.current;
    if (!img || !sc || !nat || !vw) return;
    const k = img.clientWidth / vw;
    sc.scrollTo({ top: Math.max(0, by * k - 60), left: Math.max(0, bx * k - 40), behavior: "smooth" });
  }, [nat, fit, vw, bx, by]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  return (
    <div className="fixed inset-0 z-50 flex flex-col" style={{ background: "rgba(10,9,7,0.92)" }}
      role="dialog" aria-modal="true" aria-label="Full newspaper page">
      <div className="flex items-center gap-2 px-3 sm:px-4 py-2 text-[#f0e7c6] shrink-0" style={{ background: "#14110d" }}>
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[10px] uppercase tracking-folio opacity-60 truncate">
            {[str(d.channel_name), str(d.edition_name), d.business_date ? shortDate(String(d.business_date)) : null].filter(Boolean).join(" · ")}
          </div>
          <div className="font-display text-[15px] truncate">Page {str(d.display_page_no) ?? str(d.page_no)}{d.page_count ? ` of ${str(d.page_count)}` : ""} · {str(d.headline) ?? `article ${d.article_id}`}</div>
        </div>
        <div className="flex border border-[#4a4034]">
          {(["width", "height"] as const).map((f) => (
            <button key={f} onClick={() => setFit(f)}
              className="px-2 py-1 font-mono text-[10px] uppercase tracking-folio"
              style={{ background: fit === f ? "#f0e7c6" : "transparent", color: fit === f ? "#14110d" : "#c1b596" }}>
              fit {f}
            </button>
          ))}
        </div>
        {art.page_url && (
          <a href={art.page_url} target="_blank" rel="noopener noreferrer" className="p-1.5 text-[#c1b596] hover:text-[#f0e7c6]" title="Open page image in a new tab">
            <ExternalLink size={16} />
          </a>
        )}
        <button onClick={onClose} className="p-1.5 text-[#c1b596] hover:text-[#f0e7c6]" title="Close (Esc)" aria-label="Close full page">
          <X size={20} />
        </button>
      </div>
      <div ref={scroller} className="flex-1 overflow-auto" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        {failed ? (
          <div className="h-full flex items-center justify-center font-mono text-[12px] text-[#c1b596]">
            Page image could not be loaded.
          </div>
        ) : (
          <div className="relative mx-auto" style={fit === "width" ? { width: "100%", maxWidth: 1400 } : { width: "fit-content" }}>
            {!nat && <div className="absolute inset-0 min-h-[50vh] flex items-center justify-center"><Spinner /></div>}
            <img
              ref={imgRef}
              src={art.page_url ?? undefined}
              alt="Full newspaper page"
              onLoad={(e) => setNat({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })}
              onError={() => setFailed(true)}
              style={fit === "width"
                ? { width: "100%", height: "auto", display: "block" }
                : { height: "calc(100vh - 56px)", width: "auto", maxWidth: "none", display: "block" }}
            />
            {nat && vw > 0 && vh > 0 && hole && (
              <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox={`0 0 ${vw} ${vh}`} preserveAspectRatio="none" aria-hidden>
                {/* dim everything except the article */}
                <path d={`M0,0H${vw}V${vh}H0Z ${hole}`} fill="rgba(10,9,7,0.45)" fillRule="evenodd" />
                <path d={hole} fill="none" stroke="#c34a3e" strokeWidth={2.5} vectorEffect="non-scaling-stroke" />
                {poly && bw > 0 && (
                  <rect x={bx} y={by} width={bw} height={bh} fill="none" stroke="#f5d76e" strokeDasharray="6 4" strokeWidth={1} vectorEffect="non-scaling-stroke" />
                )}
              </svg>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
